import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import { Ticket, Check, Flame, Clock, MapPin, Calendar, Minus, Plus, Crown, ChevronDown, Zap } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useCountdown } from "@/hooks/useCountdown";

const BANNERS = {
  "ep1": "/banners/ep1.jpg",
  "pglr-oct4": "/banners/oct4.jpg",
  "ep3-nov28": "/banners/ep3.jpg",
};

const Countdown = ({ target }) => {
  const t = useCountdown(target);
  if (!t) return null;
  const unit = (v, l) => (
    <div className="flex flex-col items-center">
      <span className="font-serif-display text-2xl md:text-3xl text-[#FDFBF7] tabular-nums">{String(v).padStart(2, "0")}</span>
      <span className="text-[9px] uppercase tracking-[0.2em] text-[#A19D94] mt-1">{l}</span>
    </div>
  );
  return (
    <div className="flex items-center gap-3 md:gap-4" data-testid="event-countdown">
      <Clock className="w-4 h-4 text-[#D4AF37]" />
      {unit(t.days, "Days")}<span className="text-[#8C7324] -mt-3">:</span>
      {unit(t.hours, "Hrs")}<span className="text-[#8C7324] -mt-3">:</span>
      {unit(t.mins, "Min")}<span className="text-[#8C7324] -mt-3">:</span>
      {unit(t.secs, "Sec")}
    </div>
  );
};

const BannerCarousel = ({ images, title, slug }) => {
  const [idx, setIdx] = useState(0);
  const n = images.length;
  useEffect(() => {
    if (n < 2) return;
    const t = setInterval(() => setIdx((i) => (i + 1) % n), 5000);
    return () => clearInterval(t);
  }, [n]);
  return (
    <div className="relative" data-testid={`banner-carousel-${slug}`}>
      <img src={images[0]} alt={title} className="w-full block" />
      {images.slice(1).map((src, i) => (
        <img key={src} src={src} alt="" aria-hidden="true"
          className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-700 ${idx === i + 1 ? "opacity-100" : "opacity-0"}`} />
      ))}
      {n > 1 && (
        <>
          <button type="button" aria-label="Previous flyer" data-testid={`carousel-prev-${slug}`}
            onClick={(e) => { e.stopPropagation(); setIdx((idx - 1 + n) % n); }}
            className="absolute left-3 top-1/2 -translate-y-1/2 z-10 w-9 h-9 border border-[#D8B26E]/60 bg-black/60 backdrop-blur-sm text-[#D8B26E] flex items-center justify-center hover:bg-black/85 transition-colors">
            ‹
          </button>
          <button type="button" aria-label="Next flyer" data-testid={`carousel-next-${slug}`}
            onClick={(e) => { e.stopPropagation(); setIdx((idx + 1) % n); }}
            className="absolute right-14 top-1/2 -translate-y-1/2 z-10 w-9 h-9 border border-[#D8B26E]/60 bg-black/60 backdrop-blur-sm text-[#D8B26E] flex items-center justify-center hover:bg-black/85 transition-colors">
            ›
          </button>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 z-10 flex gap-2" data-testid={`carousel-dots-${slug}`}>
            {images.map((_, i) => (
              <span key={i} className={`w-1.5 h-1.5 rounded-full transition-colors ${i === idx ? "bg-[#D8B26E]" : "bg-white/35"}`} />
            ))}
          </div>
        </>
      )}
    </div>
  );
};

export const EventsCalendar = () => {
  const [events, setEvents] = useState([]);
  const [expanded, setExpanded] = useState(null);
  const [loading, setLoading] = useState("");
  const [qty, setQty] = useState(1);
  const [ticketsMode, setTicketsMode] = useState("off");
  const [earlyCodeUi, setEarlyCodeUi] = useState(true);
  const [codes, setCodes] = useState({});
  const [emails, setEmails] = useState({});
  const [earlyForm, setEarlyForm] = useState({ email: "", phone: "" });
  const [earlyBusy, setEarlyBusy] = useState("");
  const [earlyDone, setEarlyDone] = useState({});

  useEffect(() => {
    api.get("/events").then((r) => {
      setEvents(r.data);
      const first = r.data.find((e) => e.buyable) || r.data[0];
      setExpanded(first?.slug);
    }).catch(() => {});
    api.get("/site/status").then((r) => { setTicketsMode(r.data.tickets_mode || "off"); setEarlyCodeUi(r.data.early_code_ui !== false); }).catch(() => {});
  }, []);

  const joinEarly = async (e, slug) => {
    e.preventDefault();
    if (!earlyForm.email.trim()) {
      toast.error("Enter your email to get early access.");
      return;
    }
    setEarlyBusy(slug);
    try {
      await api.post("/early-access", { email: earlyForm.email.trim(), phone: earlyForm.phone.trim() });
      setEarlyDone((d) => ({ ...d, [slug]: true }));
      setEarlyForm({ email: "", phone: "" });
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Something went wrong — try again.");
    } finally {
      setEarlyBusy("");
    }
  };

  const toggle = (slug) => {
    setExpanded((cur) => (cur === slug ? null : slug));
    setQty(1);
  };

  const buy = async (product, quantity = 1, slug = "") => {
    setLoading(product);
    try {
      const { data } = await api.post("/payments/checkout", { origin_url: window.location.origin, product, quantity, code: codes[slug] || "", email: emails[slug] || "" });
      window.location.href = data.checkout_url;
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not start checkout.");
      setLoading("");
    }
  };

  const inputCls = "w-full bg-transparent border-b border-white/20 py-3 text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors";

  return (
    <section id="events" className="relative py-24 md:py-32 px-6 md:px-10" data-testid="events-section"
      style={{ background: "linear-gradient(180deg, #070202 0%, #120505 50%, #070202 100%)" }}>
      {/* Header — Chocolate City candlelight */}
      <motion.div initial={{ opacity: 0, y: 30 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }} className="text-center mb-14 md:mb-20">
        <div className="flex items-center justify-center gap-5 mb-7">
          <span className="h-px w-16 md:w-24 bg-gradient-to-r from-transparent to-[#B3122F]" />
          <Crown className="w-6 h-6 text-[#D8B26E]" />
          <span className="h-px w-16 md:w-24 bg-gradient-to-l from-transparent to-[#B3122F]" />
        </div>
        <h2 className="font-serif-display text-5xl md:text-7xl leading-[0.95] tracking-tight text-[#FDFBF7]">
          Upcoming <span className="italic text-gradient-gold">Events</span>
        </h2>
        <div className="flex items-center justify-center gap-5 mt-7">
          <span className="h-px w-14 md:w-20 bg-[#8C7324]" />
          <p className="text-xs uppercase tracking-[0.35em] text-[#D8B26E]">The Chocolate City Takeover</p>
          <span className="h-px w-14 md:w-20 bg-[#8C7324]" />
        </div>
      </motion.div>

      {/* Banner stack */}
      <div className="max-w-[900px] mx-auto space-y-12 md:space-y-16">
        {events.map((ev, i) => {
          const isOpen = ev.slug === expanded;
          return (
            <motion.div key={ev.slug}
              initial={{ opacity: 0, y: 40 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.7, delay: i * 0.05, ease: [0.16, 1, 0.3, 1] }}
              data-testid={`event-card-${ev.slug}`}>
              {/* Date label */}
              <div className="flex items-center gap-2 text-xs uppercase tracking-[0.3em] text-[#D4AF37] mb-4">
                <Calendar className="w-4 h-4" /> {ev.date_label}
                {ev.sold_out && <span className="text-[#F4D77E] tracking-[0.2em]">· Sold Out</span>}
              </div>

              {/* Banner (carousel when the event has extra flyer designs) */}
              <div onClick={() => toggle(ev.slug)} data-testid={`event-banner-${ev.slug}`} role="button" tabIndex={0}
                onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") toggle(ev.slug); }}
                aria-expanded={isOpen} aria-controls={`event-panel-${ev.slug}`}
                className={`relative block w-full text-left border transition-colors duration-500 cursor-pointer ${
                  isOpen ? "border-[#D4AF37] gold-glow" : "border-[#D4AF37]/25 hover:border-[#D4AF37]/60"}`}>
                {(() => {
                  const imgs = (ev.banner_urls && ev.banner_urls.length)
                    ? ev.banner_urls
                    : [ev.banner_url || BANNERS[ev.slug]].filter(Boolean);
                  return imgs.length ? (
                    <BannerCarousel images={imgs} title={ev.title} slug={ev.slug} />
                  ) : (
                  <div className="w-full aspect-[43/20] bg-[#150505] flex flex-col items-center justify-center gap-3 px-6 text-center">
                    <Crown className="w-7 h-7 text-[#D4AF37]" />
                    <span className="font-serif-display text-2xl md:text-4xl text-[#FDFBF7] leading-tight">{ev.title}</span>
                  </div>
                  );
                })()}
                {ev.sold_out && (
                  <div className="absolute top-4 left-4 bg-[#5C0000] text-[#F4D77E] text-[10px] md:text-xs uppercase tracking-[0.25em] px-4 py-2 border border-[#D4AF37]/40" data-testid="sold-out-badge">
                    Sold Out
                  </div>
                )}
                {ev.buyable && !ev.sold_out && ticketsMode !== "off" && (ev.sales_enabled !== false || (ticketsMode === "early" && ev.slug === "ep3-nov28")) && (
                  <div className="absolute top-4 right-4 bg-black/70 backdrop-blur-sm text-[#F4D77E] text-[10px] md:text-xs uppercase tracking-[0.22em] px-4 py-2 border border-[#D4AF37]/40" data-testid={`price-badge-${ev.slug}`}>
                    {ev.price_label}
                  </div>
                )}
                <div className="absolute bottom-4 right-4 w-8 h-8 border border-[#D4AF37]/50 bg-black/60 backdrop-blur-sm flex items-center justify-center">
                  <ChevronDown className={`w-4 h-4 text-[#D4AF37] transition-transform duration-500 ${isOpen ? "rotate-180" : ""}`} />
                </div>
              </div>

              {/* Expandable details */}
              <AnimatePresence initial={false}>
                {isOpen && (
                  <motion.div key="detail"
                    initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
                    className="overflow-hidden">
                    <div className="border border-t-0 border-[#D4AF37]/25 bg-black/40 p-6 md:p-8" data-testid="event-detail">
                      {ev.status === "past" ? (
                        <div className="text-center py-4">
                          <p className="font-serif-display italic text-2xl text-[#F4D77E]" data-testid="event-tagline">{ev.tagline}</p>
                          <p className="text-[#A19D94] mt-3 max-w-lg mx-auto leading-relaxed">{ev.blurb}</p>
                          <p className="mt-5 inline-block text-xs uppercase tracking-[0.25em] text-[#F4D77E] border border-[#D4AF37]/40 bg-[#5C0000]/40 px-6 py-3">
                            Sold Out — This Event Has Passed
                          </p>
                        </div>
                      ) : !ev.buyable ? (
                        <div className="text-center py-4">
                          <h3 className="font-serif-display text-3xl md:text-4xl text-[#FDFBF7]">{ev.title}</h3>
                          {ev.tagline && <p className="font-serif-display italic text-lg text-[#D4AF37] mt-2" data-testid="event-tagline">{ev.tagline}</p>}
                          {ev.blurb && <p className="text-[#A19D94] mt-3 max-w-lg mx-auto leading-relaxed">{ev.blurb}</p>}
                          {ev.location && (
                            <div className="flex items-center justify-center gap-2 text-sm text-[#A19D94] mt-4">
                              <MapPin className="w-4 h-4 text-[#8C7324]" /> {ev.location}
                            </div>
                          )}
                          {ev.event_datetime && <div className="mt-6 flex justify-center"><Countdown target={ev.event_datetime} /></div>}
                          <p className="mt-6 inline-block text-xs uppercase tracking-[0.25em] text-[#D4AF37] border border-[#D4AF37]/40 px-6 py-3" data-testid="coming-soon-badge">
                            Tickets & Details Coming Soon
                          </p>
                        </div>
                      ) : (
                        (() => {
                          const showBuyStack = (ticketsMode === "early" && ev.slug === "ep3-nov28") || (ticketsMode !== "off" && ev.sales_enabled !== false);
                          const showEarly = ticketsMode === "off" && ev.slug === "ep3-nov28";
                          const showSoon = ev.buyable && ticketsMode !== "off" && ev.sales_enabled === false && !(ticketsMode === "early" && ev.slug === "ep3-nov28");
                          const onsaleFuture = ev.slug === "ep3-nov28" && ev.onsale_at && new Date(ev.onsale_at) > new Date();
                          const sideCol = showBuyStack || showEarly || showSoon;
                          return (
                        <div className={sideCol ? "grid md:grid-cols-[1.15fr_1fr] gap-8 md:gap-10" : ""}>
                          {/* Info */}
                          <div>
                            <h3 className="font-serif-display text-3xl md:text-4xl leading-[0.98] text-[#FDFBF7]">{ev.title}</h3>
                            <p className="font-serif-display italic text-lg text-[#D4AF37] mt-2" data-testid="event-tagline">{ev.tagline}</p>
                            <p className="text-[#A19D94] mt-4 leading-relaxed">{ev.blurb}</p>
                            <div className="flex items-start gap-2 text-sm text-[#A19D94] mt-4">
                              <MapPin className="w-4 h-4 text-[#8C7324] mt-0.5 shrink-0" /> {ev.location}
                            </div>
                            {onsaleFuture ? (
                              <div className="mt-6" data-testid="onsale-countdown">
                                <Countdown target={ev.onsale_at} />
                                <p className="mt-2 text-[10px] uppercase tracking-[0.25em] text-[#D4AF37]">until $25 general admission opens for everyone</p>
                              </div>
                            ) : ev.event_datetime && <div className="mt-6"><Countdown target={ev.event_datetime} /></div>}
                          </div>

                          {/* Buy stack — hidden while ticket sales are off */}
                          {showBuyStack && (
                          <div className="flex flex-col justify-end space-y-3 md:border-l md:border-white/10 md:pl-10">
                            {typeof ev.remaining === "number" && (
                              <div className="flex items-center gap-2 text-sm uppercase tracking-[0.2em] text-[#F4D77E]" data-testid="event-remaining">
                                {ev.sold_out ? "Sold Out" : (<><Flame className="w-4 h-4" />{ev.remaining} presale tickets left</>)}
                              </div>
                            )}
                            {!ev.sold_out && (
                              <div className="flex items-center justify-between border border-white/10 bg-black/30 px-5 py-3" data-testid="qty-stepper">
                                <span className="text-[10px] uppercase tracking-[0.2em] text-[#A19D94]">Tickets · max 5</span>
                                <div className="flex items-center gap-4">
                                  <button onClick={() => setQty((q) => Math.max(1, q - 1))} disabled={qty <= 1} data-testid="qty-decrease"
                                    className="w-8 h-8 border border-white/15 text-[#A19D94] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 inline-flex items-center justify-center" aria-label="Decrease quantity">
                                    <Minus className="w-3.5 h-3.5" />
                                  </button>
                                  <span className="font-serif-display text-xl text-[#FDFBF7] w-6 text-center tabular-nums" data-testid="qty-value">{qty}</span>
                                  <button onClick={() => setQty((q) => Math.min(typeof ev.remaining === "number" ? Math.min(5, ev.remaining) : 5, q + 1))}
                                    disabled={qty >= (typeof ev.remaining === "number" ? Math.min(5, ev.remaining) : 5)} data-testid="qty-increase"
                                    className="w-8 h-8 border border-white/15 text-[#A19D94] hover:border-[#D4AF37] hover:text-[#D4AF37] transition-colors disabled:opacity-30 inline-flex items-center justify-center" aria-label="Increase quantity">
                                    <Plus className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              </div>
                            )}
                            {(ticketsMode === "early" || (ticketsMode === "public" && earlyCodeUi)) && (
                              <div>
                                <input value={codes[ev.slug] || ""} onChange={(e) => setCodes((c) => ({ ...c, [ev.slug]: e.target.value.toUpperCase() }))}
                                  placeholder={ticketsMode === "early" ? "Early access code" : "Promo code (optional)"} data-testid={`code-input-${ev.slug}`}
                                  className="w-full bg-black/50 border border-[#D4AF37]/40 px-5 py-3.5 text-center text-[#F4D77E] tracking-[0.2em] uppercase placeholder-[#5c5850] placeholder:tracking-normal placeholder:normal-case focus:outline-none focus:border-[#D4AF37] transition-colors" />
                                {ticketsMode === "early" && (
                                  <p className="text-[10px] text-[#A19D94] mt-2 text-center leading-relaxed">Enter the code from your early-access email — it unlocks <span className="text-[#F4D77E]">$20 early bird</span> tickets.</p>
                                )}
                              </div>
                            )}
                            {(codes[ev.slug] || "").trim() && (
                              <input value={emails[ev.slug] || ""} onChange={(e) => setEmails((m) => ({ ...m, [ev.slug]: e.target.value }))}
                                type="email" placeholder="Email for your receipt" data-testid={`code-email-${ev.slug}`}
                                className="w-full bg-black/50 border border-white/15 px-5 py-3.5 text-center text-[#FDFBF7] placeholder-[#5c5850] focus:outline-none focus:border-[#D4AF37] transition-colors" />
                            )}
                            <button onClick={() => buy(ev.product, qty, ev.slug)} disabled={loading === ev.product || ev.sold_out || (ticketsMode === "early" && !(codes[ev.slug] || "").trim())}
                              data-testid={`buy-${ev.product}`}
                              className="w-full inline-flex items-center justify-center gap-2 text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                              <Ticket className="w-4 h-4" />
                              {ev.sold_out ? "Sold Out" : loading === ev.product ? "Redirecting…" : `Buy ${ev.price_label}`}
                            </button>
                            <p className="text-xs text-[#5c5850] text-center">Secure checkout via Stripe — no account needed.</p>
                          </div>
                          )}

                          {/* Per-event sales paused — tickets drop soon */}
                          {showSoon && (
                          <div className="flex flex-col justify-center md:border-l md:border-white/10 md:pl-10" data-testid={`sales-soon-${ev.slug}`}>
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                              <Zap className="w-4 h-4" /> Tickets Drop Soon
                            </div>
                            <p className="font-serif-display text-2xl md:text-3xl text-[#FDFBF7] mt-3">Presale for this party <span className="italic text-gradient-gold">isn't live yet</span>.</p>
                            <p className="text-sm text-[#A19D94] mt-2 leading-relaxed">
                              All the details are locked in — tickets open when Jay says go. Watch this page and our socials for the drop.
                            </p>
                          </div>
                          )}

                          {/* Early access panel — Oct 10 while sales are off */}
                          {showEarly && (
                          <div className="flex flex-col justify-center md:border-l md:border-white/10 md:pl-10" data-testid="early-panel-oct4">
                            <div className="flex items-center gap-2 text-xs uppercase tracking-[0.25em] text-[#D4AF37]">
                              <Zap className="w-4 h-4" /> Tickets Drop Soon
                            </div>
                            <p className="font-serif-display text-2xl md:text-3xl text-[#FDFBF7] mt-3">Get 24-hour <span className="italic text-gradient-gold">early access</span>.</p>
                            {!earlyDone[ev.slug] ? (
                              <>
                                <p className="text-sm text-[#A19D94] mt-2 leading-relaxed">
                                  Join the early list and you'll get a personal code to shop presale tickets a full day before anyone else.
                                </p>
                                <form onSubmit={(e) => joinEarly(e, ev.slug)} className="mt-5 space-y-3" data-testid="early-access-form-event">
                                  <input type="email" required value={earlyForm.email}
                                    onChange={(e) => setEarlyForm((f) => ({ ...f, email: e.target.value }))}
                                    placeholder="Email address" data-testid="early-event-email" className={inputCls} />
                                  <input type="tel" value={earlyForm.phone}
                                    onChange={(e) => setEarlyForm((f) => ({ ...f, phone: e.target.value }))}
                                    placeholder="Phone for SMS (optional)" data-testid="early-event-phone" className={inputCls} />
                                  <button type="submit" disabled={earlyBusy === ev.slug} data-testid="early-event-submit"
                                    className="w-full text-xs uppercase tracking-[0.25em] border border-[#D4AF37] bg-[#D4AF37] text-black px-8 py-4 hover:bg-transparent hover:text-[#D4AF37] transition-colors duration-300 disabled:opacity-50">
                                    {earlyBusy === ev.slug ? "Joining…" : "Get Early Access"}
                                  </button>
                                </form>
                              </>
                            ) : (
                              <div className="mt-5 border border-[#D4AF37]/40 bg-[#D4AF37]/5 p-5 text-center" data-testid="early-event-success">
                                <Check className="w-6 h-6 text-[#D4AF37] mx-auto mb-2" />
                                <p className="text-[#FDFBF7] font-serif-display text-xl">You're on the list.</p>
                                <p className="text-xs text-[#A19D94] mt-1">Your code lands in your inbox 24 hours before tickets go live.</p>
                              </div>
                            )}
                          </div>
                          )}
                        </div>
                          );
                        })()
                      )}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          );
        })}
      </div>
    </section>
  );
};
